import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Sparkles, X } from 'lucide-react';

const UsageWarning = ({
  uploadsRemaining,
  queriesRemaining,
  uploadsLimit,
  queriesLimit,
  planType = 'free',
  onUpgrade,
  onDismiss,
  variant = 'inline' // 'inline' | 'banner' | 'modal'
}) => {
  // Calculate warning thresholds
  const uploadWarningThreshold = Math.ceil((uploadsLimit || 5) * 0.2); // 20% remaining
  const queryWarningThreshold = Math.ceil((queriesLimit || 50) * 0.2); // 20% remaining

  const showUploadWarning = uploadsRemaining !== null && uploadsRemaining <= uploadWarningThreshold;
  const showQueryWarning = queriesRemaining !== null && queriesRemaining <= queryWarningThreshold;

  const isUrgent = uploadsRemaining === 0 || queriesRemaining === 0;
  const showWarning = (showUploadWarning || showQueryWarning) && planType === 'free';

  if (!showWarning) return null;

  const getMessage = () => {
    if (uploadsRemaining === 0 && queriesRemaining === 0) {
      return "You've reached your monthly limits for uploads and queries.";
    }
    if (uploadsRemaining === 0) {
      return "You've reached your monthly upload limit.";
    }
    if (queriesRemaining === 0) {
      return "You've reached your monthly query limit.";
    }
    if (uploadsRemaining <= uploadWarningThreshold && queriesRemaining <= queryWarningThreshold) {
      return `Running low: ${uploadsRemaining} uploads and ${queriesRemaining} queries remaining.`;
    }
    if (uploadsRemaining <= uploadWarningThreshold) {
      return `Running low: ${uploadsRemaining} upload${uploadsRemaining === 1 ? '' : 's'} remaining this month.`;
    }
    return `Running low: ${queriesRemaining} quer${queriesRemaining === 1 ? 'y' : 'ies'} remaining this month.`;
  };

  if (variant === 'banner') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`rounded-2xl border p-4 ${
            isUrgent
              ? 'border-bill-300 bg-bill-100'
              : 'border-bill-200 bg-bill-100'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={`h-5 w-5 flex-shrink-0 ${
                  isUrgent ? 'text-bill-700' : 'text-bill-600'
                }`}
              />
              <div>
                <p
                  className={`text-sm font-medium ${
                    isUrgent ? 'text-bill-800' : 'text-bill-700'
                  }`}
                >
                  {getMessage()}
                </p>
                <p className="mt-1 text-xs text-sand-700">
                  Upgrade to Pro for unlimited uploads and queries.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onUpgrade && (
                <button
                  type="button"
                  onClick={onUpgrade}
                  className="inline-flex items-center gap-1.5 rounded-full bg-bill-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm"
                >
                  <Sparkles className="h-3 w-3" />
                  Upgrade
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-lg p-1.5 text-bill-600 hover:bg-bill-200 hover:text-bill-800"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (variant === 'inline') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${
          isUrgent
            ? 'border border-bill-300 bg-bill-100 text-bill-800'
            : 'border border-bill-200 bg-bill-100 text-bill-700'
        }`}
      >
        <AlertTriangle className="h-3 w-3" />
        <span>{getMessage()}</span>
        {onUpgrade && (
          <button
            type="button"
            onClick={onUpgrade}
            className="font-semibold underline hover:no-underline"
          >
            Upgrade
          </button>
        )}
      </motion.div>
    );
  }

  // Modal variant
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          className="w-full max-w-md rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-lg"
        >
          <div className="text-center">
            <div
              className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
                isUrgent ? 'bg-bill-200' : 'bg-bill-100'
              }`}
            >
              <AlertTriangle
                className={`h-6 w-6 ${isUrgent ? 'text-bill-700' : 'text-bill-600'}`}
              />
            </div>

            <h3 className="text-lg font-semibold text-ink mb-2">
              {isUrgent ? 'Usage limit reached' : 'Running low on usage'}
            </h3>

            <p className="text-sm text-sand-700 mb-4">{getMessage()}</p>

            <div className="rounded-xl border border-[rgba(32,30,29,0.12)] bg-sand-100 p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-ink">
                    {uploadsRemaining ?? '--'}
                  </div>
                  <div className="text-xs text-sand-600">uploads left</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-ink">
                    {queriesRemaining ?? '--'}
                  </div>
                  <div className="text-xs text-sand-600">queries left</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {onUpgrade && (
                <button
                  type="button"
                  onClick={onUpgrade}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-bill-500 px-4 py-3 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm"
                >
                  <Sparkles className="h-4 w-4" />
                  Upgrade to Pro - Unlimited usage
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="w-full rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-4 py-3 text-sm font-semibold text-ink hover:bg-sand-100 transition-colors"
                >
                  Maybe later
                </button>
              )}
            </div>

            <p className="mt-4 text-xs text-sand-600">
              Limits reset on the 1st of each month
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UsageWarning;
